/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThumbsUp, ShieldCheck, Camera, ChevronDown, Star } from 'lucide-react';
import StarRating from './StarRating';
import ReviewForm from './ReviewForm';
import ReviewLightbox from './ReviewLightbox';
import { reviewsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const SORTS = [
  { key: 'recent', label: 'Most Recent' },
  { key: 'highest', label: 'Highest Rating' },
  { key: 'lowest', label: 'Lowest Rating' },
  { key: 'helpful', label: 'Most Helpful' },
  { key: 'photos', label: 'With Photos' },
];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

const ReviewsSection = ({ product }) => {
  const { user } = useAuth();
  const [summary, setSummary] = useState({ average: 0, total: 0, breakdown: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 } });
  const [reviews, setReviews] = useState([]);
  const [sort, setSort] = useState('recent');
  const [starFilter, setStarFilter] = useState(null); // 1-5 or null
  const [withPhotos, setWithPhotos] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [eligibility, setEligibility] = useState({ can_review: false, verified_purchase: false, already_reviewed: false });
  const [lightbox, setLightbox] = useState({ images: [], index: 0, open: false });
  const [helpfulVoted, setHelpfulVoted] = useState({}); // reviewId -> voted bool (local UI)

  const pid = product?.id;

  const loadSummary = useCallback(async () => {
    if (!pid) return;
    try {
      setSummary(await reviewsApi.summary(pid));
    } catch {}
  }, [pid]);

  const loadReviews = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const params = { sort };
      if (starFilter) params.star = starFilter;
      if (withPhotos) params.with_photos = true;
      setReviews(await reviewsApi.list(pid, params));
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [pid, sort, starFilter, withPhotos]);

  const loadEligibility = useCallback(async () => {
    if (!pid || !user) {
      setEligibility({ can_review: false, verified_purchase: false, already_reviewed: false });
      return;
    }
    try {
      setEligibility(await reviewsApi.eligibility(pid));
    } catch {}
  }, [pid, user]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadReviews(); }, [loadReviews]);
  useEffect(() => { loadEligibility(); }, [loadEligibility]);

  // Inject JSON-LD for SEO whenever product/summary/reviews change
  useEffect(() => {
    if (!product) return;
    const id = `ld-product-${product.id}`;
    let existing = document.getElementById(id);
    if (existing) existing.remove();

    const data = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': product.name,
      'image': product.image,
      'description': product.description,
      'sku': product.id,
      'brand': { '@type': 'Brand', 'name': 'VELOUVA' },
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'USD',
        'price': product.price?.toFixed?.(2) ?? String(product.price ?? ''),
        'availability': (product.stock || 0) > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      },
    };
    if (summary.total > 0) {
      data.aggregateRating = {
        '@type': 'AggregateRating',
        'ratingValue': summary.average.toFixed(1),
        'reviewCount': summary.total,
        'bestRating': '5',
        'worstRating': '1',
      };
    }
    if (reviews.length > 0) {
      data.review = reviews.slice(0, 8).map((r) => ({
        '@type': 'Review',
        'author': { '@type': 'Person', 'name': r.user_name },
        'datePublished': r.created_at,
        'name': r.title,
        'reviewBody': r.text,
        'reviewRating': {
          '@type': 'Rating',
          'ratingValue': String(r.rating),
          'bestRating': '5',
          'worstRating': '1',
        },
      }));
    }
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [product, summary, reviews]);

  const handleHelpful = async (reviewId) => {
    if (!user) return;
    try {
      const res = await reviewsApi.toggleHelpful(reviewId);
      setHelpfulVoted((cur) => ({ ...cur, [reviewId]: res.voted }));
      setReviews((cur) => cur.map((r) => r.id === reviewId ? { ...r, helpful_count: res.helpful_count } : r));
    } catch {}
  };

  const openLightbox = (images, index) => setLightbox({ images, index, open: true });

  const maxBreakdown = useMemo(() => {
    return Math.max(1, ...Object.values(summary.breakdown || {}));
  }, [summary]);

  const renderWriteCTA = () => {
    if (!user) {
      return (
        <p className="text-sm text-[#5a4a3e]">
          <Link to="/login" className="underline">Sign in</Link> to leave a review.
        </p>
      );
    }
    if (!eligibility.verified_purchase) {
      return <p className="text-sm text-[#5a4a3e]">Only verified customers can review this shade.</p>;
    }
    if (eligibility.already_reviewed) {
      return <p className="text-sm text-[#5a4a3e]">You've already shared your thoughts on this shade — thank you.</p>;
    }
    return (
      <button onClick={() => setShowForm(true)}
        className="bg-[#1a1410] text-[#f5f1e6] px-8 py-3 text-[11px] tracking-[0.32em] hover:bg-black transition-colors">
        WRITE A REVIEW
      </button>
    );
  };

  return (
    <section className="px-6 lg:px-10 py-20 border-t border-[#e5dfd0]">
      <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">
        CLIENT REVIEWS
      </div>
      <h2 className="font-serif text-[40px] lg:text-[52px] leading-[1.05] mt-6">
        What our customer <span className="italic">says</span>.
      </h2>

      {/* Summary panel */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
        <div>
          <div className="flex items-baseline gap-3">
            <div className="font-serif text-[68px] leading-none">{summary.average.toFixed(1)}</div>
            <div className="text-sm text-[#7a6a5a]">/ 5.0</div>
          </div>
          <div className="mt-3">
            <StarRating value={summary.average} size={20} />
          </div>
          <p className="mt-3 text-sm text-[#5a4a3e]">
            {summary.total === 0 ? 'No reviews yet — be the first to share.' : `${summary.total} verified ${summary.total === 1 ? 'review' : 'reviews'}`}
          </p>
        </div>

        <div className="lg:col-span-1">
          <div className="space-y-2.5">
            {[5, 4, 3, 2, 1].map((s) => {
              const count = summary.breakdown[String(s)] || 0;
              const pct = (count / maxBreakdown) * 100;
              const active = starFilter === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStarFilter(active ? null : s)}
                  className={`w-full flex items-center gap-3 text-left group ${active ? 'font-medium' : ''}`}
                  aria-label={`Filter by ${s} stars`}
                >
                  <span className="text-xs w-12 text-[#3a2e26] tracking-wide flex items-center gap-1">
                    {s} <Star className="w-3 h-3" fill="#1a1410" strokeWidth={0} />
                  </span>
                  <span className="flex-1 h-1.5 bg-[#e5dfd0] relative overflow-hidden">
                    <span
                      className={`absolute inset-y-0 left-0 transition-all ${active ? 'bg-[#1a1410]' : 'bg-[#7a6a5a] group-hover:bg-[#1a1410]'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="text-xs w-8 text-right text-[#5a4a3e]">{count}</span>
                </button>
              );
            })}
          </div>
          {starFilter && (
            <button onClick={() => setStarFilter(null)} className="mt-4 text-[10px] tracking-[0.22em] text-[#7a6a5a] hover:text-[#1a1410]">
              CLEAR STAR FILTER ×
            </button>
          )}
        </div>

        <div className="lg:col-span-1 flex flex-col items-start lg:items-end gap-3">
          {renderWriteCTA()}
        </div>
      </div>

      {/* Controls: sort + with photos */}
      <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-[#e5dfd0] py-4">
        <div className="flex items-center gap-3">
          <span className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">SORT BY</span>
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="appearance-none bg-transparent border border-[#cdc2b1] focus:border-[#1a1410] pl-3 pr-9 py-2 text-xs tracking-[0.18em] uppercase focus:outline-none"
            >
              {SORTS.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.4} />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase cursor-pointer">
          <input type="checkbox" checked={withPhotos} onChange={(e) => setWithPhotos(e.target.checked)} className="accent-[#1a1410]" />
          <Camera className="w-4 h-4" strokeWidth={1.4} /> WITH PHOTOS
        </label>
        {starFilter && (
          <span className="text-xs tracking-[0.18em] uppercase text-[#3a2e26]">
            Showing {starFilter}-star reviews
          </span>
        )}
      </div>

      {/* Review list */}
      <div className="mt-2 divide-y divide-[#e5dfd0]">
        {loading ? (
          <p className="py-12 text-center text-[#7a6a5a]">Loading reviews…</p>
        ) : reviews.length === 0 ? (
          <p className="py-12 text-center text-[#7a6a5a]">No reviews match those filters yet.</p>
        ) : (
          reviews.map((r) => {
            const voted = helpfulVoted[r.id];
            return (
              <article key={r.id} className="py-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <StarRating value={r.rating} size={16} />
                      <span className="font-serif text-lg text-[#1a1410]">{r.title}</span>
                      {r.featured && (
                        <span className="text-[9px] tracking-[0.22em] bg-[#1a1410] text-[#f5f1e6] px-2 py-0.5">FEATURED</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-xs text-[#5a4a3e]">
                      <span className="font-medium text-[#1a1410]">{r.user_name}</span>
                      {r.verified_purchase && (
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] text-[#3a6a3a]">
                          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.6} /> VERIFIED PURCHASE
                        </span>
                      )}
                      <span>·</span>
                      <span>{formatDate(r.created_at)}</span>
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-[15px] leading-[1.7] text-[#3a2e26] whitespace-pre-wrap">{r.text}</p>

                {r.images && r.images.length > 0 && (
                  <div className="mt-4 flex gap-2 flex-wrap">
                    {r.images.map((src, idx) => (
                      <button key={idx} type="button" onClick={() => openLightbox(r.images, idx)}
                        className="w-20 h-20 overflow-hidden border border-[#cdc2b1] hover:border-[#1a1410] transition-colors">
                        <img src={src} alt={`Review photo ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-5">
                  <button
                    onClick={() => handleHelpful(r.id)}
                    disabled={!user}
                    title={user ? '' : 'Sign in to vote'}
                    className={`inline-flex items-center gap-2 text-[11px] tracking-[0.22em] hover:text-black ${voted ? 'text-[#1a1410]' : 'text-[#7a6a5a]'} disabled:cursor-not-allowed`}
                  >
                    <ThumbsUp className="w-3.5 h-3.5" strokeWidth={1.4} fill={voted ? '#1a1410' : 'none'} />
                    HELPFUL ({r.helpful_count || 0})
                  </button>
                </div>
              </article>
            );
          })
        )}
      </div>

      {showForm && (
        <ReviewForm
          productId={pid}
          onClose={() => setShowForm(false)}
          onSubmitted={() => { loadSummary(); loadReviews(); loadEligibility(); }}
        />
      )}

      {lightbox.open && (
        <ReviewLightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox({ ...lightbox, open: false })}
          onIndexChange={(i) => setLightbox({ ...lightbox, index: i })}
        />
      )}
    </section>
  );
};

export default ReviewsSection;
